/**
 * Transfer Products → Typesense document builder.
 *
 * Reads from core.product_item (joined with transfer_request for certificate_number)
 * and builds search documents for the transfer-products Typesense collection.
 */
import {
  SQSClient,
  SendMessageCommand,
  MessageAttributeValue,
} from "@aws-sdk/client-sqs"
import {
  coreProductItem,
  coreContainer,
  transferRequest,
} from "@tc/drizzle-v2"
import { eq, inArray } from "drizzle-orm"
import { DbTransaction } from "@tc/middy-decorators"
import { ProductAttributes } from "../types/core"

const sqs_client = new SQSClient({})
const queue_url = process.env["TRANSFER_PRODUCTS_QUEUE_URL"]

const extractId = (obj: unknown): string =>
  obj && typeof obj === "object" && "id" in obj ? String(obj.id) : ""

const extractName = (obj: unknown): string =>
  obj && typeof obj === "object" && "name" in obj ? String(obj.name) : ""

interface TransferProductDoc {
  id: string
  item_description: string
  net_weight?: string | null
  net_volume?: string | null
  eligibility_Countries?: string[]
  container_number?: string | null
  container_seals?: string[]
  NZHSC_code?: string | null
  productionBatch_ids?: string[]
  package_quantity?: string | null
  package_type?: string | null
  item_marks?: string[]
  transfer_id: string
  certificate_number: string
  unofficial_commercial_ref?: string | null
  comments?: string | null
  sequence_number?: number | null
  updated_at?: string | null
}

const buildTransferProductDoc = (
  product: {
    id: string
    description: unknown
    attributes: unknown
    packaging: unknown
    eligibilityCountries: unknown
    productReference: string | null
    groupSequence: number | null
    updatedAt: Date
  },
  transferId: string,
  certificateNumber: string,
  container?: { containerNumber: string | null; seals: unknown } | null,
): TransferProductDoc => {
  const attrs: ProductAttributes =
    product.attributes && typeof product.attributes === "object"
      ? (product.attributes as ProductAttributes)
      : {}
  const netWeight = attrs.netWeight
  const netVolume = attrs.netVolume
  const netWeightUnit =
    netWeight && typeof netWeight === "object" && netWeight.unit
      ? extractId(netWeight.unit)
      : ""
  const netVolumeUnit =
    netVolume && typeof netVolume === "object" && netVolume.unit
      ? extractId(netVolume.unit)
      : ""

  const packaging = Array.isArray(product.packaging) ? product.packaging : []
  const firstPkg = packaging[0] as
    | { quantity?: number | null; packageType?: string | null; itemMarksOuter?: string | null }
    | undefined

  return {
    id: product.id,
    item_description: product.groupSequence
      ? `${product.groupSequence}: ${extractName(product.description)}`
      : extractName(product.description),
    net_weight:
      netWeight && typeof netWeight === "object" && netWeight.value
        ? `${netWeight.value} ${netWeightUnit}`.trim()
        : null,
    net_volume:
      netVolume && typeof netVolume === "object" && netVolume.value
        ? `${netVolume.value} ${netVolumeUnit}`.trim()
        : null,
    eligibility_Countries: Array.isArray(product.eligibilityCountries)
      ? product.eligibilityCountries
          .flatMap((c: unknown) => {
            if (typeof c === "string") return [c]
            return [extractId(c), extractName(c)]
          })
          .filter(Boolean)
      : [],
    container_number: container?.containerNumber ?? null,
    container_seals: Array.isArray(container?.seals) ? (container.seals as string[]) : [],
    NZHSC_code: extractId(attrs.NZHSCode) || null,
    productionBatch_ids: Array.isArray(attrs.productionBatchIds) ? attrs.productionBatchIds : [],
    package_quantity: firstPkg?.quantity != null ? String(firstPkg.quantity) : null,
    package_type: firstPkg?.packageType ? extractId(firstPkg.packageType) : null,
    item_marks: packaging
      .map((pkg: { itemMarksOuter?: string | null }) => pkg?.itemMarksOuter)
      .filter((m: unknown): m is string => Boolean(m) && typeof m === "string"),
    transfer_id: transferId,
    certificate_number: certificateNumber,
    unofficial_commercial_ref: typeof product.productReference === "string" ? product.productReference : null,
    comments: typeof (attrs as Record<string, unknown>)["productStatement"] === "string"
      ? (attrs as Record<string, unknown>)["productStatement"] as string
      : null,
    sequence_number: product.groupSequence ?? null,
    updated_at: product.updatedAt?.toISOString() ?? null,
  }
}

export const saveTransferProductsToSearch = async (
  tx: DbTransaction,
  id: string,
  collectionName: string,
  options?: { returnDoc?: boolean },
): Promise<void> => {
  const result = await getTransferProductsBulkData(tx, [id])
  const doc = result.get(id)
  if (!doc) return

  if (options?.returnDoc) return doc as never

  const attributes: Record<string, MessageAttributeValue> = {
    collection: { StringValue: collectionName, DataType: "String" },
  }

  await sqs_client.send(
    new SendMessageCommand({
      QueueUrl: queue_url,
      MessageBody: JSON.stringify(doc),
      MessageAttributes: attributes,
    }),
  )
}

export const getTransferProductsBulkData = async (
  tx: DbTransaction,
  ids: readonly string[],
): Promise<Map<string, TransferProductDoc>> => {
  if (ids.length === 0) return new Map()

  // 1. Batch fetch product items
  const products = await tx
    .select({
      id: coreProductItem.id,
      productGroupId: coreProductItem.productGroupId,
      description: coreProductItem.description,
      attributes: coreProductItem.attributes,
      packaging: coreProductItem.packaging,
      eligibilityCountries: coreProductItem.eligibilityCountries,
      productReference: coreProductItem.productReference,
      groupSequence: coreProductItem.groupSequence,
      updatedAt: coreProductItem.updatedAt,
    })
    .from(coreProductItem)
    .where(inArray(coreProductItem.id, ids as string[]))

  if (products.length === 0) return new Map()

  // 2. Batch fetch containers
  const productIds = products.map((p) => p.id)
  const containers = await tx
    .select({
      productItemId: coreContainer.productItemId,
      containerNumber: coreContainer.containerNumber,
      seals: coreContainer.seals,
    })
    .from(coreContainer)
    .where(inArray(coreContainer.productItemId, productIds))

  const containersByProduct = new Map<string, { containerNumber: string | null; seals: unknown }>()
  for (const c of containers) {
    if (c.productItemId) containersByProduct.set(c.productItemId, c)
  }

  // 3. Batch fetch transfer requests via product groups
  const groupIds = [...new Set(products.map((p) => p.productGroupId))]
  const transfers = await tx
    .select({
      productGroupId: transferRequest.productGroupId,
      id: transferRequest.id,
      certificateNumber: transferRequest.certificate_number,
    })
    .from(transferRequest)
    .where(inArray(transferRequest.productGroupId, groupIds))

  const transferByGroup = new Map<string, { id: string; certificateNumber: string }>()
  for (const t of transfers) {
    transferByGroup.set(t.productGroupId, { id: t.id, certificateNumber: t.certificateNumber })
  }

  // 4. Build docs
  const result = new Map<string, TransferProductDoc>()
  for (const product of products) {
    const transfer = transferByGroup.get(product.productGroupId)
    if (!transfer) continue

    const doc = buildTransferProductDoc(
      product as Parameters<typeof buildTransferProductDoc>[0],
      transfer.id,
      transfer.certificateNumber,
      containersByProduct.get(product.id),
    )
    result.set(product.id, doc)
  }
  return result
}
